import { z } from 'zod';
import { openWorldCardFlagsSchema } from './cards';
import { OPEN_WORLD_MOB_SPECIES_MAP_COUNT, openWorldMobMapBand } from './mobs';

/**
 * **Os bosses do mundo aberto: uma a três presenças por quadrante perigoso.**
 *
 * ## O que os separa de um mob
 *
 * Um mob é uma criatura de percurso: nasce numa vaga, persegue quem passa, paga
 * meio ouro e volta cinco minutos depois. Um boss é um **evento**. Ele tem três
 * vezes a vida que teria num mapa normal, guarda um pacote de um a seis itens
 * que só o último golpe leva inteiro, recupera tudo se for abandonado meia hora,
 * e demora uma hora a voltar. Enfrentá-lo é uma decisão, e desistir a meio não
 * deixa nada preso.
 *
 * ## De onde vêm os números
 *
 * **Do catálogo da caça, e de lado nenhum.** Tipo, vida por mapa, dano,
 * velocidade, raio, cadência e escala são lidos de lá em cada uso. O que este
 * gate acrescenta por cima é UMA regra, exata e aplicada uma só vez: a vida do
 * boss no mundo aberto é a vida canónica daquele mapa e daquele tamanho **vezes
 * três**. Nada mais é triplicado — nem dano, nem velocidade, nem cadência, nem
 * raio, nem escala.
 *
 * A faixa de mapas vem da MESMA partição que as criaturas do quadrante já usam
 * (\`openWorldMapBandOfDanger\`), e não de uma segunda tabela.
 *
 * ## O que este ficheiro decide, e o que não
 *
 * Aqui está o **contrato**: os dois relógios, o papel de cada vaga, a forma
 * pública e a derivação das chaves. Alcance de aggro, trela, intervalo entre
 * magias e perfil de carga são equilíbrio, vivem em \`open-world/room/balance.ts\`
 * e são provisórios.
 */

/* -------------------------------------------------------------------------- */
/*  Os dois relógios                                                          */
/* -------------------------------------------------------------------------- */

/**
 * **Uma hora de relógio real depois de uma morte concluída, e o boss volta.**
 *
 * Tempo de parede e não tempo observado: um quadrante vazio não corre tiques, e
 * um boss que só voltasse enquanto alguém o estivesse a ver nunca voltaria. O
 * prazo sobrevive a uma suspensão, a uma reconstrução do objeto durável e a uma
 * reconexão, e nenhuma delas o reinicia.
 */
export const OPEN_WORLD_BOSS_RESPAWN_MS = 60 * 60 * 1_000;

/**
 * **Trinta minutos verdadeiramente abandonado, e a vida volta ao máximo.**
 *
 * "Abandonado" exige as três coisas ao mesmo tempo: nenhum alvo elegível,
 * nenhum jogador hostil dentro da região de combate, e nenhum dano recebido. Um
 * único golpe reinicia a contagem, e um jogador parado ao lado dele impede-a de
 * sequer começar.
 *
 * A recuperação **não é uma morte**: não paga recompensa, não abre uma geração
 * nova, não re-rola o pacote e não desenha explosão nenhuma. Ela existe para
 * que um boss deixado a um ponto de vida não fique a ser um troféu de graça
 * para quem passar a seguir.
 */
export const OPEN_WORLD_BOSS_ABANDON_MS = 30 * 60 * 1_000;

/**
 * **Três.** A vida de um boss do mundo aberto, em relação à do mapa normal.
 *
 * Fixo e aprovado, não provisório. Multiplicado UMA vez, em inteiro, no
 * instante em que a geração nasce — e guardado, para uma sala reconstruída não
 * o multiplicar outra vez.
 */
export const OPEN_WORLD_BOSS_HEALTH_MULTIPLIER = 3n;

/* -------------------------------------------------------------------------- */
/*  O pacote                                                                  */
/* -------------------------------------------------------------------------- */

/** Quantos itens um boss guarda, inclusive. Fixo e aprovado. */
export const OPEN_WORLD_BOSS_ITEMS_MIN = 1;
export const OPEN_WORLD_BOSS_ITEMS_MAX = 6;

/**
 * A versão do selo do pacote.
 *
 * Ela entra na etiqueta do fluxo secreto, portanto mudá-la muda **o conteúdo de
 * todas as gerações futuras** de todos os bosses. É por isso que é uma
 * constante nomeada e não um literal escondido numa concatenação: subi-la é uma
 * decisão, e tem de ser tomada por alguém.
 */
export const OPEN_WORLD_BOSS_SEAL_VERSION = 1;

/* -------------------------------------------------------------------------- */
/*  O papel                                                                   */
/* -------------------------------------------------------------------------- */
/*  A identidade dos vinte bosses                                             */
/* -------------------------------------------------------------------------- */

/**
 * **Os vinte índices de mapa que um boss pode ter.**
 *
 * Não é uma escala aberta: são exatamente os mapas do alfa fechado, que é de
 * onde `openWorldBossMapIndex` só sabe tirar índices. Escrito como união de
 * literais para a relação abaixo poder ser total — omitir um boss deixa de
 * compilar, e acrescentar um índice que não existe também.
 */
export type OpenWorldBossMapIndex =
  | 1
  | 2
  | 3
  | 4
  | 5
  | 6
  | 7
  | 8
  | 9
  | 10
  | 11
  | 12
  | 13
  | 14
  | 15
  | 16
  | 17
  | 18
  | 19
  | 20;

/** O maior índice que um boss pode ter. O tamanho da relação, dito uma vez. */
export const OPEN_WORLD_BOSS_MAP_INDEX_MAX = 20;

/**
 * **Os três arquétipos.**
 *
 * `melee` fere por contacto. `caster` mantém distância e usa magia. `final` é um
 * dos três últimos: um kit superior, com uma perseguição inevitável, um golpe de
 * pressão em intervalo sorteado e persistido pelo servidor, e um derradeiro que
 * mata sem uma mecânica de sobrevivência anunciada.
 *
 * O arquétipo é DESIGN. Ele não traz um número: nem dano, nem alcance, nem
 * duração, nem velocidade, nem cadência. Esses vivem no equilíbrio, e é lá que
 * são decididos.
 */
export const OPEN_WORLD_BOSS_ARCHETYPES = ['melee', 'caster', 'final'] as const;

export type OpenWorldBossArchetype = (typeof OPEN_WORLD_BOSS_ARCHETYPES)[number];

/** Os golpes exclusivos dos corpo a corpo. Um por boss, e nenhum implementado. */
export const OPEN_WORLD_BOSS_MELEE_BLOWS = [
  'stinger_lunge',
  'circular_judgment',
  'ember_roar',
  'chain_whirl',
  'twin_chains',
  'reaping_fan',
] as const;

export type OpenWorldBossMeleeBlow = (typeof OPEN_WORLD_BOSS_MELEE_BLOWS)[number];

/**
 * As magias exclusivas dos conjuradores. Uma por boss, e as onze implementadas.
 *
 * **Nenhuma delas é a Esfera de Fogo nem a Lança Solar.** Essas duas eram
 * provisórias, partilhadas por todos os conjuradores, e foram removidas no gate
 * V28I.BG assim que o último kit definitivo passou a existir.
 */
export const OPEN_WORLD_BOSS_CASTER_SPELLS = [
  'crown_of_ashes',
  'blood_seal',
  'putrid_breath',
  'ocular_beam',
  'swarm_stance',
  'cross_of_lament',
  'hunger',
  'choir_of_faces',
  'rotary_fire',
  'five_maws',
  'hypnotic_dust',
] as const;

export type OpenWorldBossCasterSpell = (typeof OPEN_WORLD_BOSS_CASTER_SPELLS)[number];

/** A perseguição inevitável de um final. */
export const OPEN_WORLD_BOSS_FINAL_HOMINGS = [
  'abyssal_harpoon',
  'judgment_spear',
  'crown_gaze',
] as const;

export type OpenWorldBossFinalHoming = (typeof OPEN_WORLD_BOSS_FINAL_HOMINGS)[number];

/** O golpe de pressão de um final, em intervalo sorteado e persistido. */
export const OPEN_WORLD_BOSS_FINAL_PRESSURES = [
  'depth_bombardment',
  'choral_volley',
  'pack_bite',
] as const;

export type OpenWorldBossFinalPressure = (typeof OPEN_WORLD_BOSS_FINAL_PRESSURES)[number];

/** O derradeiro de um final. Mata, e tem sempre uma saída anunciada. */
export const OPEN_WORLD_BOSS_FINAL_ULTIMATES = ['abyssal_collapse', 'sentence', 'decree'] as const;

export type OpenWorldBossFinalUltimate = (typeof OPEN_WORLD_BOSS_FINAL_ULTIMATES)[number];

/**
 * **O kit de um boss**, com a forma que o arquétipo dele exige e nenhuma outra.
 *
 * União discriminada de propósito: um corpo a corpo tem UM golpe e não tem
 * magia; um conjurador tem UMA magia e não tem golpe; um final tem os três
 * campos e não tem nem golpe nem magia soltos. A coerência não é uma convenção
 * que alguém tem de lembrar — é o que o compilador aceita.
 *
 * **Nenhum kit é uma carta de jogador.** Um golpe de boss não entra no baralho
 * de ninguém, e um identificador daqui nunca é o identificador de uma carta.
 */
export type OpenWorldBossKit =
  | { readonly archetype: 'melee'; readonly blow: OpenWorldBossMeleeBlow }
  | { readonly archetype: 'caster'; readonly spell: OpenWorldBossCasterSpell }
  | {
      readonly archetype: 'final';
      readonly homing: OpenWorldBossFinalHoming;
      readonly pressure: OpenWorldBossFinalPressure;
      readonly ultimate: OpenWorldBossFinalUltimate;
    };

/**
 * **A relação canónica: quem é cada um dos vinte bosses.**
 *
 * ## A identidade é o mapa, e mais nada
 *
 * O arquétipo de um boss é uma propriedade do BOSS, indexada pelo `mapIndex` — a
 * identidade canónica que já decide a arte, a vida e a espécie. Não da vaga, não
 * do quadrante, não da linha, não da coluna, não da semente e não da geração. O
 * boss 9 é corpo a corpo em qualquer quadrante do mundo, depois de qualquer
 * renascimento e depois de qualquer reconstrução da sala.
 *
 * Isto **substitui** a alternância por vaga que existia antes, e que dava ao
 * mesmo boss papéis diferentes em quadrantes diferentes: quem aprendia a lutar
 * com ele num sítio não tinha aprendido nada para o sítio seguinte.
 *
 * ## O que ela não é
 *
 * Não copia as bandas de perigo: a banda continua a decidir QUE bosses um
 * quadrante pode ter, e esta tabela só diz o que cada um deles é. Não traz
 * números. Não olha para os pixels da arte. E não dá nomes oficiais a ninguém —
 * os bosses continuam deliberadamente sem nome, tal como os setores.
 *
 * ## Porque é total
 *
 * `Record<OpenWorldBossMapIndex, …>`: falta um índice e não compila, sobra um e
 * não compila. Um boss novo no catálogo obriga alguém a decidir o que ele é, em
 * vez de o deixar cair num ramo por omissão.
 */
export const OPEN_WORLD_BOSS_IDENTITIES: {
  readonly [K in OpenWorldBossMapIndex]: OpenWorldBossKit;
} = Object.freeze({
  1: { archetype: 'caster', spell: 'crown_of_ashes' },
  2: { archetype: 'caster', spell: 'blood_seal' },
  3: { archetype: 'caster', spell: 'putrid_breath' },
  4: { archetype: 'caster', spell: 'ocular_beam' },
  5: { archetype: 'caster', spell: 'swarm_stance' },
  6: { archetype: 'caster', spell: 'cross_of_lament' },
  7: { archetype: 'caster', spell: 'hunger' },
  8: { archetype: 'caster', spell: 'choir_of_faces' },
  9: { archetype: 'melee', blow: 'stinger_lunge' },
  10: { archetype: 'caster', spell: 'rotary_fire' },
  11: { archetype: 'melee', blow: 'circular_judgment' },
  12: { archetype: 'melee', blow: 'ember_roar' },
  13: { archetype: 'melee', blow: 'chain_whirl' },
  14: { archetype: 'caster', spell: 'five_maws' },
  15: { archetype: 'melee', blow: 'twin_chains' },
  16: { archetype: 'melee', blow: 'reaping_fan' },
  17: { archetype: 'caster', spell: 'hypnotic_dust' },
  18: {
    archetype: 'final',
    homing: 'abyssal_harpoon',
    pressure: 'depth_bombardment',
    ultimate: 'abyssal_collapse',
  },
  19: {
    archetype: 'final',
    homing: 'judgment_spear',
    pressure: 'choral_volley',
    ultimate: 'sentence',
  },
  20: {
    archetype: 'final',
    homing: 'crown_gaze',
    pressure: 'pack_bite',
    ultimate: 'decree',
  },
} as const);

/**
 * **O kit de um mapa**, ou uma recusa.
 *
 * Recusa e não adapta, pela mesma razão que `openWorldMobMapIndex` recusa: um
 * índice fora da relação é um catálogo que cresceu sem que ninguém tenha dito o
 * que os bosses novos são, e escolher por eles em silêncio daria um arquétipo
 * arbitrário a um boss que alguém desenhou.
 */
export function openWorldBossKit(mapIndex: number): OpenWorldBossKit {
  if (OPEN_WORLD_BOSS_MAP_INDEX_MAX !== OPEN_WORLD_MOB_SPECIES_MAP_COUNT) {
    throw new RangeError(
      `open world boss identities cover ${OPEN_WORLD_BOSS_MAP_INDEX_MAX} maps; the catalogue now has ${OPEN_WORLD_MOB_SPECIES_MAP_COUNT}`,
    );
  }
  const kit = (
    OPEN_WORLD_BOSS_IDENTITIES as Readonly<Record<number, OpenWorldBossKit | undefined>>
  )[mapIndex];
  if (kit === undefined) {
    throw new RangeError(`open world boss map index ${mapIndex} has no identity`);
  }
  return kit;
}

/** O arquétipo de um mapa. A leitura direta da relação, e a única. */
export function openWorldBossArchetype(mapIndex: number): OpenWorldBossArchetype {
  return openWorldBossKit(mapIndex).archetype;
}

/**
 * As seis magias do gate V28I.BC — os conjuradores 1 a 6.
 *
 * Duas listas e não uma porque os perfis vivem em duas tabelas: cada gate
 * escreveu a sua, e juntá-las depois seria perder de vista quem decidiu o quê.
 */
export const OPEN_WORLD_BOSS_EARLY_SPELLS = [
  'crown_of_ashes',
  'blood_seal',
  'putrid_breath',
  'ocular_beam',
  'swarm_stance',
  'cross_of_lament',
] as const;

export type OpenWorldBossEarlySpell = (typeof OPEN_WORLD_BOSS_EARLY_SPELLS)[number];

/** As cinco magias do gate V28I.BE — os conjuradores 7, 8, 10, 14 e 17. */
export const OPEN_WORLD_BOSS_LATE_SPELLS = [
  'hunger',
  'choir_of_faces',
  'rotary_fire',
  'five_maws',
  'hypnotic_dust',
] as const;

export type OpenWorldBossLateSpell = (typeof OPEN_WORLD_BOSS_LATE_SPELLS)[number];

/** O teleguiado de um final, ou `null`. */
export function openWorldBossHomingAbility(mapIndex: number): OpenWorldBossHomingAbility | null {
  const kit = openWorldBossKit(mapIndex);
  return kit.archetype === 'final' ? (kit.homing as OpenWorldBossHomingAbility) : null;
}

/** A ultimate de um final, ou `null`. */
export function openWorldBossUltimateAbility(mapIndex: number): OpenWorldBossUltimate | null {
  const kit = openWorldBossKit(mapIndex);
  return kit.archetype === 'final' ? (kit.ultimate as OpenWorldBossUltimate) : null;
}

/**
 * **A habilidade que a máquina corre para este mapa.** Uma, e sempre.
 *
 * A pergunta única, feita antes de tudo o resto, e **total desde o gate
 * V28I.BG**: o golpe de um corpo a corpo, a magia de um conjurador, ou o golpe
 * de pressão de um final. Não devolve `null` porque não existe boss nenhum sem
 * habilidade própria — a alternância provisória entre a Esfera de Fogo e a
 * Lança Solar, que era para onde um kit por construir caía, foi removida.
 *
 * O `switch` é exaustivo sobre o arquétipo, portanto um arquétipo novo não
 * compila enquanto alguém não disser o que a máquina corre para ele.
 */
export function openWorldBossAbility(mapIndex: number): OpenWorldBossStrikeAbility {
  const kit = openWorldBossKit(mapIndex);
  switch (kit.archetype) {
    case 'melee':
      return kit.blow;
    case 'caster':
      return kit.spell;
    case 'final':
      return kit.pressure;
  }
}

/* -------------------------------------------------------------------------- */

/**
 * **O que um boss faz quando alcança alguém.**
 *
 * Duas formas, porque a simulação só sabe fazer duas coisas: um corpo a corpo
 * persegue e fere por contacto; um conjurador mantém distância e usa magia.
 */
export const OPEN_WORLD_BOSS_ROLES = ['melee', 'caster'] as const;

export type OpenWorldBossRole = (typeof OPEN_WORLD_BOSS_ROLES)[number];

/**
 * **O papel operacional de um boss**, derivado da identidade dele e de mais nada.
 *
 * Este é o degrau que traduz três arquétipos nas duas coisas que a simulação
 * sabe fazer. É um `switch` sobre a relação canónica e **não uma segunda
 * tabela**: um mapa que mude de arquétipo muda de papel no mesmo instante, sem
 * que exista um sítio onde as duas coisas possam discordar.
 *
 * Um `final` **não conjura** desde o gate V28I.BG: o kit dele existe inteiro, e
 * o papel só diz a duas coisas o que fazer — que ele não fere por contacto, e
 * que o cliente lhe dá o sinal discreto de quem ataca de longe.
 */
export function openWorldBossRole(mapIndex: number): OpenWorldBossRole {
  const archetype = openWorldBossArchetype(mapIndex);
  switch (archetype) {
    case 'melee':
      return 'melee';
    case 'caster':
      return 'caster';
    /*
     * **Um final não conjura** (gate V28I.BG). O kit dele existe inteiro — um
     * teleguiado, um golpe de pressão e uma ultimate — e o papel operacional
     * dele é o do golpe: ele mantém distância e ataca de longe, como um
     * conjurador, mas nada nele passa pelo caminho das magias provisórias.
     */
    case 'final':
      return 'caster';
  }
}

/* -------------------------------------------------------------------------- */
/*  O mapa                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * **Que mapa uma vaga de boss usa.** A MESMA faixa que as criaturas do
 * quadrante já usam, e não uma segunda tabela.
 *
 * A partição por perigo é uma propriedade do quadrante e não dos mobs: perigo 1
 * dá os mapas 1 a 4, perigo 5 dá 17 a 20, e um boss de um quadrante tem de ser
 * do mesmo mundo que os alienígenas que andam à volta dele. Reaproveitar a
 * função é o que impede as duas coisas de discordarem.
 *
 * ## A escolha DENTRO da faixa vive noutro sítio (gate V28I.BW.5A)
 *
 * Aqui ficou só a partição. Qual das quatro identidades da faixa uma vaga usa
 * é `openWorldMapIndexInBand`, com uma permutação derivada da semente privada
 * do mundo e o ordinal do quadrante na grelha.
 *
 * A dobra que aqui estava só lia `(perigo, vaga)`: a coordenada não entrava,
 * portanto todos os quadrantes do mesmo perigo davam o mesmo trio — e sete dos
 * vinte mapas de chefe nunca eram produzidos por quadrante nenhum, incluindo o
 * **18**, um dos três finais.
 */
export function openWorldBossMapBand(danger: number): {
  readonly first: number;
  readonly count: number;
} {
  return openWorldMapBandOfDanger(danger);
}

/**
 * A faixa de mapas de um grau de perigo. **Um alias nomeado, não uma cópia.**
 *
 * A partição foi escrita no gate dos mobs porque foram eles que a precisaram
 * primeiro, mas ela é do QUADRANTE: um boss e um alienígena do mesmo sítio têm
 * de sair do mesmo conjunto de mapas. Este nome existe para que quem lê o
 * código dos bosses não tenha de importar uma função com "mob" no nome e
 * perguntar-se se está a partilhar a coisa certa.
 */
export const openWorldMapBandOfDanger = openWorldMobMapBand;

/* -------------------------------------------------------------------------- */
/*  As identidades das operações                                              */
/* -------------------------------------------------------------------------- */

const SEPARATOR = '|';

/**
 * **O id da adoção do pacote de uma geração**, derivado e nunca cunhado.
 *
 * Mundo, vaga e geração. **A conta não entra**, e é deliberado: uma geração
 * produz um pacote e um só, e a conta é o DESTINATÁRIO que o último golpe
 * escolheu — não parte da identidade do que aconteceu. Se ela entrasse na
 * chave, duas tentativas fatais de jogadores diferentes sobre o mesmo boss
 * produziriam dois ids, e a idempotência deixaria de proteger o que existe para
 * proteger: **um pacote por geração**.
 *
 * A vaga já carrega o quadrante — o id dela é \`{linha}:{coluna}:boss:{ordinal}\`
 * — e a geração é a vida. A mesma vaga que morre outra vez tem outra geração e
 * outra chave.
 */
export function openWorldBossPackageEventId(input: {
  readonly worldId: string;
  readonly slotId: string;
  readonly generation: number;
}): string {
  return ['boss-package', input.worldId, input.slotId, String(input.generation)].join(SEPARATOR);
}

/* -------------------------------------------------------------------------- */
/*  A forma pública                                                           */
/* -------------------------------------------------------------------------- */

const bossIdSchema = z.string().min(1).max(64);

/* -------------------------------------------------------------------------- */
/*  Os golpes físicos dos corpo a corpo                                       */
/* -------------------------------------------------------------------------- */

/**
 * **A versão do vocabulário dos golpes físicos.**
 *
 * Fechada e própria: a forma persistida de um golpe e a forma pública dele
 * mudam juntas, e um número que só diz respeito a elas é o que evita subir o
 * protocolo inteiro quando só um golpe ganhou uma etapa.
 */
export const OPEN_WORLD_BOSS_ABILITY_VERSION = 4;

/**
 * **As habilidades que a máquina autoritativa corre** (gate V28I.BG).
 *
 * Vinte, uma por boss: os seis golpes físicos dos corpo a corpo, as onze magias
 * exclusivas dos conjuradores e os três golpes de pressão dos finais. Não é uma
 * segunda lista: cada nome também está no kit da relação canónica
 * (`OPEN_WORLD_BOSS_IDENTITIES`), e o compilador recusa qualquer um que não
 * esteja nos dois sítios.
 *
 * "Strike" deixou de querer dizer "golpe físico": quer dizer **uma habilidade de
 * boss com telegraph**. A máquina, as fases, o congelamento e a lista de vítimas
 * por etapa são os mesmos para um leque de lâminas e para uma coroa de fogo, e
 * ter duas máquinas para a mesma coisa era ter duas que divergiriam.
 *
 * Os três teleguiados e as três ultimates **não estão aqui**, e é deliberado:
 * um teleguiado é um corpo com relógio próprio e uma ultimate tem uma máquina
 * ao lado. Elas têm as listas e os contratos delas.
 */
export const OPEN_WORLD_BOSS_STRIKE_ABILITIES = [
  /* Os seis corpo a corpo (gate V28I.BA). */
  'stinger_lunge',
  'circular_judgment',
  'ember_roar',
  'chain_whirl',
  'twin_chains',
  'reaping_fan',
  /* As seis magias exclusivas dos conjuradores 1 a 6 (gate V28I.BC). */
  'crown_of_ashes',
  'blood_seal',
  'putrid_breath',
  'ocular_beam',
  'swarm_stance',
  'cross_of_lament',
  /* As cinco magias dos conjuradores 7, 8, 10, 14 e 17 (gate V28I.BE). */
  'hunger',
  'choir_of_faces',
  'rotary_fire',
  'five_maws',
  'hypnotic_dust',
  /* Os três golpes de pressão dos finais 18, 19 e 20 (gate V28I.BG). */
  'depth_bombardment',
  'choral_volley',
  'pack_bite',
] as const;

export type OpenWorldBossStrikeAbility = (typeof OPEN_WORLD_BOSS_STRIKE_ABILITIES)[number];

/**
 * **As quatro fases de um golpe.**
 *
 * `windup` é o que o corpo faz antes de anunciar — recuar, investir, travar — e
 * ainda é evitável por distância. `telegraph` é o anúncio: o boss está imóvel e
 * a geometria já está congelada, portanto o que o jogador vê é exactamente o que
 * o vai atingir. `active` executa as etapas. `recovery` é o preço de ter
 * atacado.
 *
 * O contacto básico está suspenso nas quatro.
 */
export const OPEN_WORLD_BOSS_STRIKE_PHASES = ['windup', 'telegraph', 'active', 'recovery'] as const;

export type OpenWorldBossStrikePhase = (typeof OPEN_WORLD_BOSS_STRIKE_PHASES)[number];

/**
 * **A forma de acerto de uma etapa**, publicada para o desenho ser o que magoa.
 *
 * `capsule` é um varrimento entre dois pontos com meia-largura; `ring` é um anel
 * que fecha; `disc` é um círculo inteiro; `sector` é um cone com meio-ângulo.
 * O cliente desenha a partir daqui e de mais lado nenhum — não há uma segunda
 * descrição da geometria no cliente que possa discordar desta.
 */
export const OPEN_WORLD_BOSS_STRIKE_SHAPES = [
  'capsule',
  'ring',
  'disc',
  'sector',
  /**
   * **Quatro faixas que atravessam o boss e cobram uma vez** (gate V28I.BC).
   *
   * Uma forma própria e não quatro cápsulas separadas, porque o que a define é
   * partilharem a MESMA lista de vítimas: um casco no cruzamento central recebe
   * uma aplicação, e nunca quatro.
   */
  'cross',
  /**
   * **Uma frente que EXPANDE, e só a banda dela fere** (gate V28I.BE).
   *
   * Uma forma própria, e não um `ring` com outro sentido. O anel da Coroa fecha
   * e varre por cima de tudo o que está lá dentro, portanto o teste dele é
   * "dentro do raio exterior". Uma onda do Coro passa: ela fere quem estiver na
   * espessura da frente no instante em que ela lá está, e ficar **entre duas**
   * tem de ser realmente seguro. Sobrecarregar `ring` faria as duas discordarem
   * sobre o que "anel" quer dizer.
   */
  'wave',
] as const;

export type OpenWorldBossStrikeShape = (typeof OPEN_WORLD_BOSS_STRIKE_SHAPES)[number];

/**
 * **O golpe em curso, tal como a autoridade o descreve.**
 *
 * Tudo o que o cliente precisa para desenhar o aviso e a execução no sítio
 * exacto: a habilidade, a fase, o progresso dela em milésimos, a etapa, a
 * origem e a direcção CONGELADAS, e a geometria autoritativa.
 *
 * **Não viaja dano.** Nem o valor, nem quem vai apanhar, nem quem apanhou. Um
 * cliente que soubesse o dano antes de ele acontecer saberia uma coisa que não
 * lhe serve para jogar e que serve para prever o servidor.
 */
/**
 * **A etapa mais alta que um golpe de boss pode publicar** (gate V28I.CG).
 *
 * Era um 15 escrito à mão, e o Fogo Rotativo (`rotary_fire`) tem 25 etapas: nas
 * etapas 16 a 25 cada fotografia com o golpe falhava a validação no cliente e
 * era deitada fora inteira, em silêncio — cascos, corpos, formas de salva e
 * eventos com ela. A regra canónica vive nos perfis das habilidades
 * (`OPEN_WORLD_BOSS_ABILITY_PROFILES`, em `game-domain`), que este pacote não
 * pode importar; por isso o número vive aqui e `boss-strike-wire.test.ts` exige
 * que ele seja EXATAMENTE a maior contagem de etapas desses perfis, e que cada
 * etapa de cada habilidade passe pelo contrato do fio.
 */
export const OPEN_WORLD_BOSS_STRIKE_STAGES_MAX = 25;

export const openWorldBossStrikeSchema = z
  .object({
    k: z.enum(OPEN_WORLD_BOSS_STRIKE_ABILITIES),
    p: z.enum(OPEN_WORLD_BOSS_STRIKE_PHASES),
    /** O progresso DESTA fase, em milésimos. Cresce, e o desenho segue-o. */
    c: z.number().int().min(0).max(1_000),
    /**
     * A etapa em curso. Zero fora de `active`; em `active`, de 1 às etapas do
     * perfil da habilidade — até {@link OPEN_WORLD_BOSS_STRIKE_STAGES_MAX}.
     */
    st: z.number().int().min(0).max(OPEN_WORLD_BOSS_STRIKE_STAGES_MAX),
    /** A forma de acerto desta etapa. */
    sh: z.enum(OPEN_WORLD_BOSS_STRIKE_SHAPES),
    /** A origem congelada, em coordenadas locais do quadrante. */
    ox: z.number().int().min(0),
    oy: z.number().int().min(0),
    /** A direcção congelada. Normalizada em inteiros, como a mira de um casco. */
    dx: z.number().int(),
    dy: z.number().int(),
    /**
     * **Os pontos auxiliares congelados**, em pares `x, y`.
     *
     * Três habilidades os usam, e todas pela mesma razão: a geometria foi
     * decidida no telegraph e o desenho tem de ser exactamente ela.
     *
     * - **Duas Correntes**: `P1` e `P2`. A segunda maça parte de `P1` e não de
     *   onde o boss está agora.
     * - **Bombardeio das Profundezas** (versão 29): os **quatro** círculos, um
     *   por obus. Sem eles o navegador teria de repetir o espalhamento — uma
     *   segunda descrição da mecânica.
     * - **Salva Coral** (versão 29): os **sete** pontos de fim das lâminas. As
     *   folgas entre elas são a resposta inteira à habilidade, e um leque
     *   recalculado podia discordar delas por um arredondamento.
     *
     * O tecto são catorze números — sete pares, o maior dos três.
     */
    pt: z.array(z.number().int().min(0)).max(14).optional(),
    /** O alcance autoritativo desta etapa, em milli-unidades. */
    rr: z.number().int().min(0),
    /** A meia-largura da cápsula, ou zero quando a forma não a tem. */
    hw: z.number().int().min(0),
    /**
     * **O raio INTERIOR de uma onda** (versão 28), em milli-unidades.
     *
     * Zero nas outras formas. Uma onda fere a banda entre este raio e `rr`, e é
     * isso que faz ficar ENTRE duas ondas ser realmente seguro — o cliente
     * desenha exactamente a banda que magoa.
     */
    in: z.number().int().min(0),
    /**
     * O meio-ângulo de um sector, em **basis points da tangente**.
     *
     * Inteiro e sem trigonometria: o teste é `|cross| x 10 000 <= dot x an`, que
     * é o mesmo em qualquer motor. Zero quando a forma não é um sector.
     */
    an: z.number().int().min(0),
  })
  .strict();

export type OpenWorldBossStrike = z.infer<typeof openWorldBossStrikeSchema>;

/**
 * **Uma zona perigosa no chão** (gate V28I.BC), deixada por uma habilidade.
 *
 * Uma só forma para as duas que existem — as brasas do Rugido, discos; e a
 * névoa do Sopro Pútrido, um sector — porque são o mesmo conceito e duas
 * infraestruturas paralelas para o mesmo conceito divergem na primeira correção.
 *
 * Ela é um perigo e não um efeito: tem posição, forma, raio e prazo
 * autoritativos, e quem a desenha desenha o que magoa. Morre com o boss que a
 * deixou — não há dano póstumo — e zonas do mesmo boss não acumulam.
 */
export const openWorldBossEmberSchema = z
  .object({
    i: z.number().int().min(1),
    /** De quem ela é. Zonas de bosses diferentes são fontes diferentes. */
    b: bossIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    r: z.number().int().min(1),
    /** `disc` ou `sector`. As outras formas não persistem no chão. */
    sh: z.enum(['disc', 'sector']),
    /** A direcção congelada de um sector. Zero num disco. */
    dx: z.number().int(),
    dy: z.number().int(),
    /** O meio-ângulo em basis points da tangente. Zero num disco. */
    an: z.number().int().min(0),
    /**
     * **A lentidão que esta zona impõe**, em basis points (versão 28).
     *
     * Zero é "não abranda nada", que é o que as brasas e a névoa sempre foram.
     * O Pó Hipnótico traz 5 500: metade e um pouco da velocidade em vigor, e a
     * composição é o MENOR multiplicador — nunca o produto de duas lentidões.
     */
    sl: z.number().int().min(0).max(10_000),
    /**
     * **Os dois relógios AUTORITATIVOS**, em tiques da sala.
     *
     * E não um milésimo do que resta: um milésimo obrigaria o cliente a contar o
     * tempo dele a partir do instante em que a trama chegou, e o §13 do gate
     * proíbe-o. Com o nascimento e o prazo, o desenho interpola contra o `k` que
     * a mesma trama já traz — o relógio da sala, e não o do navegador.
     */
    bt: z.number().int().min(0),
    et: z.number().int().min(0),
  })
  .strict();

export type OpenWorldBossEmber = z.infer<typeof openWorldBossEmberSchema>;

/* -------------------------------------------------------------------------- */
/*  Os três bosses finais (gate V28I.BG)                                      */
/* -------------------------------------------------------------------------- */

/**
 * **Os três teleguiados inevitáveis**, e **as três ultimates**.
 *
 * Aliases do kit, e não segundas listas: os nomes já estão em
 * `OPEN_WORLD_BOSS_IDENTITIES` através de `OPEN_WORLD_BOSS_FINAL_HOMINGS` e
 * `OPEN_WORLD_BOSS_FINAL_ULTIMATES`, e duas listas com os mesmos nomes seriam
 * duas a divergir no dia em que alguém acrescentasse um quarto final.
 *
 * Existem com este nome porque é assim que a máquina fala deles — o kit diz
 * quem são, e estes dizem o que a simulação corre.
 */
export const OPEN_WORLD_BOSS_HOMING_ABILITIES = OPEN_WORLD_BOSS_FINAL_HOMINGS;

export type OpenWorldBossHomingAbility = OpenWorldBossFinalHoming;

export const OPEN_WORLD_BOSS_ULTIMATES = OPEN_WORLD_BOSS_FINAL_ULTIMATES;

export type OpenWorldBossUltimate = OpenWorldBossFinalUltimate;

/**
 * **As três fases de uma ultimate.**
 *
 * `telegraph` anuncia e é onde a mecânica de sobrevivência é lida; `channel` é
 * a janela em que ela pode ser cumprida; `resolve` é o instante em que a
 * autoridade decide, e ele dura um tique.
 *
 * Nomeadas, e não um literal repetido: a forma pública e o descodificador do
 * registo leem a MESMA lista, e duas listas seriam duas a divergir.
 */
export const OPEN_WORLD_BOSS_ULTIMATE_PHASES = ['telegraph', 'channel', 'resolve'] as const;

export type OpenWorldBossUltimatePhase = (typeof OPEN_WORLD_BOSS_ULTIMATE_PHASES)[number];

/**
 * **Como uma ultimate acabou.**
 *
 * `pending` enquanto corre. `interrupted` quando os jogadores cumpriram a
 * mecânica. `executed` quando falharam. `cancelled` quando não havia ninguém
 * para a sofrer — e essa não consome marco nenhum.
 */
export const OPEN_WORLD_BOSS_ULTIMATE_OUTCOMES = [
  'pending',
  'interrupted',
  'executed',
  'cancelled',
] as const;

export type OpenWorldBossUltimateOutcome = (typeof OPEN_WORLD_BOSS_ULTIMATE_OUTCOMES)[number];

/**
 * **Um teleguiado no ar**, como quem o vê o conhece.
 *
 * Ele não viaja no canal dos corpos voadores porque não é um deles: não colide
 * com nada senão o alvo, não é destrutível e não expira. O que viaja é a
 * posição, o rumo e de quem ele é — nunca o dano, nunca o alvo.
 */
export const openWorldBossHomingSchema = z
  .object({
    i: z.number().int().min(1),
    /**
     * **A quem ele vai**, pela entidade pública que o espectador já conhece.
     *
     * O ALVO, e não o dono. O comentário dizia "de quem ele é" e estava errado
     * desde o princípio: a sala escreve aqui `targetEntityId` e o cliente lê-o
     * como alvo, portanto os dois lados sempre concordaram e só a prosa é que
     * discordava. Corrigida a prosa, e não a trama — subir um protocolo para
     * acompanhar um comentário enganado seria partir clientes por causa de uma
     * frase.
     *
     * A autoria não viaja aqui. Quem o lançou é o boss do encontro, e o campo
     * onde ela viveria não existe nesta trama.
     */
    o: z.number().int().min(1),
    k: z.enum(OPEN_WORLD_BOSS_HOMING_ABILITIES),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Para onde ele aponta AGORA. Ele recompõe o rumo a cada tique. */
    dx: z.number().int(),
    dy: z.number().int(),
    r: z.number().int().min(1),
  })
  .strict();

export type OpenWorldBossHoming = z.infer<typeof openWorldBossHomingSchema>;

/**
 * **Um núcleo rúnico da Sentença.**
 *
 * Não é item, não é mob, não é boss e não é recurso mineral: é uma coisa com
 * vida que existe durante dez segundos e não paga nada a ninguém. A vida viaja
 * para a barra dele poder ser desenhada; o limiar não existe aqui.
 */
export const openWorldBossCoreSchema = z
  .object({
    i: z.number().int().min(1),
    b: bossIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    r: z.number().int().min(1),
    /** A vida actual e a máxima, em milli, como decimais canónicos. */
    h: z.string().min(1).max(40),
    hm: z.string().min(1).max(40),
  })
  .strict();

export type OpenWorldBossCore = z.infer<typeof openWorldBossCoreSchema>;

/**
 * **A ultimate em curso, como o campo a vê.**
 *
 * O progresso da interrupção viaja em MILÉSIMOS e nunca em absoluto: o limiar é
 * do servidor, e publicá-lo daria ao cliente um número que ele não usa para
 * jogar e que serve para prever a autoridade.
 */
export const openWorldBossUltimateSchema = z
  .object({
    k: z.enum(OPEN_WORLD_BOSS_ULTIMATES),
    /** \`telegraph\` a anunciar, \`channel\` a decorrer, ou a resolver-se. */
    p: z.enum(OPEN_WORLD_BOSS_ULTIMATE_PHASES),
    /** O progresso DESTA fase, em milésimos. */
    c: z.number().int().min(0).max(1_000),
    /** O progresso da interrupção, em milésimos. Zero nas que não têm limiar. */
    ip: z.number().int().min(0).max(1_000),
    /** O anel do Decreto: as duas bordas, em milli-unidades. Zero nas outras. */
    ri: z.number().int().min(0),
    ro: z.number().int().min(0),
    /**
     * **Este casco está envolvido.**
     *
     * Privado por construção: cada ligação recebe a resposta sobre SI, e nunca
     * a lista. Uma lista de vítimas na trama seria dizer a toda a gente quem
     * vai morrer.
     */
    iv: z.boolean(),
  })
  .strict();

export type OpenWorldBossUltimateState = z.infer<typeof openWorldBossUltimateSchema>;

/**
 * **Um boss que entrou na vista.** O que não muda enquanto ele viver.
 *
 * Note o que NÃO está aqui: quantos itens ele guarda, e quais. O pacote é
 * segredo até à tentativa fatal, e o campo para ele não existe — que é o que
 * torna impossível revelá-lo por descuido.
 */
export const openWorldBossEntrySchema = z
  .object({
    i: bossIdSchema,
    g: z.number().int().min(1),
    /**
     * O mapa de onde as estatísticas desta vaga saíram.
     *
     * É o **tipo visual**: o cliente resolve por ele o sprite e a escala, pelo
     * mesmo catálogo que a caça usa. Publicar o índice em vez de um id de
     * recurso é o que faz a arte dos vinte mapas aparecer sozinha no dia em que
     * ela existir, sem mexer neste ficheiro.
     */
    m: z.number().int().min(1).max(50),
    /** Corpo a corpo, ou conjurador. O cliente distingue-os com um sinal discreto. */
    r: z.enum(OPEN_WORLD_BOSS_ROLES),
    /** A escala desta geração, em basis points. A variação canónica de tamanho. */
    s: z.number().int().min(1),
    /** O raio de colisão autoritativo, em milli-unidades. */
    rd: z.number().int().min(1),
    /** A vida máxima em milli, como decimal. Já com o triplo aplicado. */
    hm: z.string().min(1).max(40),
  })
  .strict();

export type OpenWorldBossEntry = z.infer<typeof openWorldBossEntrySchema>;

/** Um boss já na vista que mudou. Um boss parado e calado não aparece. */
export const openWorldBossStateSchema = z
  .object({
    i: bossIdSchema,
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /** Para onde ele aponta. Normalizada, como a mira de um casco. */
    dx: z.number().int(),
    dy: z.number().int(),
    /** A vida atual em milli, como decimal. */
    h: z.string().min(1).max(40),
    /** Está envenenado. Um bit, e não a pilha: a pilha é de quem a lançou. */
    p: z.boolean(),
    /** Está a sangrar. */
    b: z.boolean(),
    /** As penalidades de uma carta, nas mesmas letras de um casco (versão 16). */
    ...openWorldCardFlagsSchema.shape,
    /**
     * **A habilidade em curso, ou nada** (versão 26).
     *
     * Um boss só corre uma de cada vez, e nunca ao mesmo tempo que `u`: uma
     * ultimate impede qualquer ataque novo, portanto as duas máquinas nunca
     * estão preenchidas juntas.
     *
     * O campo `c` — a conjuração provisória partilhada por todos os
     * conjuradores — **deixou de existir na versão 29**, com a habilidade que o
     * enchia.
     */
    s: openWorldBossStrikeSchema.nullable(),
    /**
     * **A ultimate em curso, ou nada** (versão 29). Só um final a tem.
     *
     * Ela corre ao lado de `s` e não dentro: enquanto ela existir, `s` é nulo,
     * porque uma ultimate impede qualquer ataque novo.
     */
    u: openWorldBossUltimateSchema.nullable(),
  })
  .strict();

export type OpenWorldBossState = z.infer<typeof openWorldBossStateSchema>;

/** Um boss que saiu da vista, ou que deixou de existir. */
export const openWorldBossGoneSchema = bossIdSchema;

/** O que aconteceu a um boss, desenhado uma vez e esquecido. */
export const OPEN_WORLD_BOSS_EVENTS = [
  /** Um golpe aterrou nele. */
  'hit',
  /** Encostou-se a alguém e feriu. Só um corpo a corpo o faz. */
  'contact',
  /** Um corpo saiu: um teleguiado de um final foi lançado e já não muda de dono. */
  'cast',
  /** Um corpo acertou: uma bala rebentou, ou um teleguiado encontrou o casco. */
  'impact',
  /**
   * **Uma etapa de um golpe físico executou-se** (versão 26).
   *
   * Um por etapa e não um por vítima: a etapa é a coisa que acontece, e quem
   * ela apanhou já aparece pelo dano no casco de cada um. O raio vem junto
   * quando a etapa teve área.
   */
  'strike',
  /** Uma ultimate começou a anunciar-se (versão 29). */
  'ultimate',
  /** Ela executou-se: quem falhou a mecânica morreu. */
  'ultimate_executed',
  /** Ela foi interrompida: os jogadores cumpriram a mecânica. */
  'ultimate_interrupted',
  /** Morreu. */
  'death',
  /** Nasceu, ou renasceu. */
  'spawn',
  /** Recuperou por abandono. **Não é uma morte**, e não se desenha como uma. */
  'restored',
] as const;

export type OpenWorldBossEventKind = (typeof OPEN_WORLD_BOSS_EVENTS)[number];

/** Um evento, deduplicável pelo ordinal da sua geração. */
export const openWorldBossEventSchema = z
  .object({
    i: bossIdSchema,
    n: z.number().int().min(1),
    k: z.enum(OPEN_WORLD_BOSS_EVENTS),
    x: z.number().int().min(0),
    y: z.number().int().min(0),
    /**
     * **O raio da explosão, em milli-unidades** (versão 21). Só num `impact`
     * que teve uma área, e ausente em todos os outros.
     *
     * Existe porque o `impact` de uma Esfera Ígnea e o de uma parcela da Lança
     * Solar são a mesma palavra e coisas de tamanhos diferentes: a esfera abre
     * uma área que fere toda a gente lá dentro, e a parcela é um golpe num
     * casco. Sem este número o desenho abria sempre a mesma bola — e ela era um
     * valor fixo escrito no cliente, portanto o que se via não era o que feria.
     *
     * É o raio **MECÂNICO**, o mesmo com que a sala escolheu as vítimas: o
     * desenho abre exatamente até onde o dano chegou. Ausente quer dizer "não
     * houve área", e o desenho fica o que sempre foi.
     */
    r: z.number().int().min(1).optional(),
  })
  .strict();

export type OpenWorldBossEvent = z.infer<typeof openWorldBossEventSchema>;

/**
 * **A recusa por falta de espaço, dita SÓ ao candidato.**
 *
 * O único sítio em que a quantidade de itens de um boss é revelada — e ela é
 * revelada a quem acabou de dar o golpe fatal e não coube com o pacote, porque
 * dizer-lhe "não coube" sem dizer quanto faltava seria dizer-lhe nada.
 *
 * Ninguém mais recebe isto. Um espetador não fica a saber quantos itens aquele
 * boss guarda, e o boss fica a um ponto de vida à espera de outro golpe.
 */
export const openWorldBossCapacityRefusalSchema = z
  .object({
    i: bossIdSchema,
    /** Quantos espaços o pacote inteiro precisa. */
    need: z.number().int().min(OPEN_WORLD_BOSS_ITEMS_MIN).max(OPEN_WORLD_BOSS_ITEMS_MAX),
    /** Quantos espaços a carga de campo tinha livres. */
    free: z.number().int().min(0),
  })
  .strict();

export type OpenWorldBossCapacityRefusal = z.infer<typeof openWorldBossCapacityRefusalSchema>;
